import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';

interface DisableForwardingDialogProps {
  channelName: string;
  /**
   * The string the user must type exactly to enable the confirm button.
   * Defaults to `channelName` when omitted (preserving existing behaviour for
   * non-WhatsApp callers where the display name contains no un-typeable chars).
   *
   * Pass the display phone number for WhatsApp channels so the token is both
   * number-specific (D9 requirement) and keyboard-typeable (the composite
   * `channelDisplayName` contains a middot · U+00B7 that is not reachable from
   * a standard keyboard without copy-paste).
   */
  confirmToken?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void | Promise<void>;
  loading: boolean;
}

export function DisableForwardingDialog({
  channelName,
  confirmToken,
  open,
  onOpenChange,
  onConfirm,
  loading,
}: DisableForwardingDialogProps) {
  const [confirmText, setConfirmText] = useState('');

  const token = confirmToken ?? channelName;
  const confirmed = confirmText === token;

  return (
    <AlertDialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!loading) {
          onOpenChange(nextOpen);
          if (!nextOpen) setConfirmText('');
        }
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            Disable messaging for {channelName}?
          </AlertDialogTitle>
          <AlertDialogDescription>
              Disabling will stop sending and receiving immediately.
              Any events received while disabled are permanently lost, we do
              not save them.
              You will lose access to send messages from this number.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="grid gap-2 px-0">
          <label className="text-sm font-medium">
            Type &ldquo;{token}&rdquo; to confirm:
          </label>
          <Input
            type="text"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder={`Type ${token} to confirm`}
            aria-label="Type channel name to confirm disabling messaging"
            autoComplete="off"
          />
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel>Keep Messaging</AlertDialogCancel>
          <AlertDialogAction
            variant="destructive"
            disabled={!confirmed || loading}
            onClick={onConfirm}
          >
            {loading && <Loader2 className="size-3.5 animate-spin" />}
            {loading ? 'Disabling...' : 'Disable Messaging'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
